import { useState, useRef, ChangeEvent } from "react";
import { TextField, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { debounce } from "lodash";
import { CardData, ScannerInputValue } from "@/interfaces";
import { isDigit, isAlphanumeric, isCode128 } from "@/utils";
import { CardNumberInput, ScannerInput } from "../../atoms";
import * as S from "./styles";

const TestPage: React.FC = () => {
  const [debounceValue, setDebounceValue] = useState(1000);
  const [codeLengthValue, setCodeLengthValue] = useState<
    number | number[] | undefined
  >(4);
  const [testFnValue, setTestFnValue] = useState({ testFn: isCode128 });
  const [textFieldLog, setTextFieldLog] = useState("");
  const [scannerInputLog, setScannerInputLog] = useState("");

  const handleDebounceChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const valueStr = e.target.value;
    if (!valueStr) {
      setDebounceValue(0);
      return;
    }

    let value;

    try {
      value = parseInt(valueStr, 10);
    } catch {
      value = 0;
    }

    setDebounceValue(value);
  };

  const handleCodeLengthChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const valueStr = e.target.value;
    if (!valueStr) return;

    if (valueStr.indexOf(",") !== -1) {
      let values: number[] = [];

      const valuesStr = valueStr.split(",");
      for (let i = 0, l = valuesStr.length; i < l; ++i) {
        let value: number;

        try {
          value = parseInt(valuesStr[i], 10);
          if (value > 0) values.push(value);
        } catch (e) {
          continue;
        }
      }

      if (values.length) setCodeLengthValue(values);
    } else {
      let value: number;

      try {
        value = parseInt(valueStr, 10);
        if (value > 0) setCodeLengthValue(value);
      } catch (e) {
        return;
      }
    }
  };

  const handleTestFnChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const valueStr = e.target.value;
    if (!valueStr) return;

    switch (valueStr) {
      case "isDigit":
        setTestFnValue({ testFn: isDigit });
        break;
      case "isAlphanumeric":
        setTestFnValue({ testFn: isAlphanumeric });
        break;
      case "isCode128":
        setTestFnValue({ testFn: isCode128 });
        break;
      default:
        setTestFnValue({ testFn: isDigit });
        break;
    }
  };

  const handleTextFieldChange = (value?: CardData) => {
    console.log(value);
    setTextFieldLog(
      (prev) =>
        `${prev}\r\n${
          value
            ? `{cardNumber:${value.cardNumber}, expiration: "${value.expiration}"}`
            : "undefined | null"
        }`
    );
  };

  const handleScannerInputChange = (value?: ScannerInputValue) => {
    console.log(value);
    setScannerInputLog(
      (prev) =>
        `${prev}\r\n${
          value
            ? `{isCode:${value.isCode}, value: "${value.value}"}`
            : "undefined | null"
        }`
    );
  };

  return (
    <S.TestPage>
      <S.DebounceHeaderContainer>Debounce</S.DebounceHeaderContainer>
      <S.DebounceContainer>
        <TextField value={debounceValue} onChange={handleDebounceChange} />
      </S.DebounceContainer>
      <S.CodeLengthHeaderContainer>codeLength</S.CodeLengthHeaderContainer>
      <S.CodeLengthContainer>
        <TextField value={codeLengthValue} onChange={handleCodeLengthChange} />
      </S.CodeLengthContainer>
      <S.TestFnHeaderContainer>testFn</S.TestFnHeaderContainer>
      <S.TestFnContainer>
        <RadioGroup
          row
          aria-labelledby="test-fn-radio-group"
          defaultValue="isCode128"
          onChange={handleTestFnChange}
        >
          <FormControlLabel
            value="isDigit"
            control={<Radio />}
            label="isDigit"
            labelPlacement="start"
          />
          <FormControlLabel
            value="isAlphanumeric"
            control={<Radio />}
            label="isAlphanumeric"
            labelPlacement="start"
          />
          <FormControlLabel
            value="isCode128"
            control={<Radio />}
            label="isCode128"
            labelPlacement="start"
          />
        </RadioGroup>
      </S.TestFnContainer>
      <S.TextFieldHeaderContainer>Credit Card</S.TextFieldHeaderContainer>
      <S.ScannerInputHeaderContainer>
        ScannerInput
      </S.ScannerInputHeaderContainer>
      <S.TextFieldContainer>
        <CardNumberInput
          onChange={handleTextFieldChange}
          numberLength={[15, 16]}
          placeholder="Swipe or enter credit card"
          fullWidth
        />
      </S.TextFieldContainer>
      <S.ScannerInputContainer>
        <ScannerInput
          onChange={handleScannerInputChange}
          wait={debounceValue}
          codeLength={codeLengthValue}
          fullWidth
          testFn={testFnValue.testFn}
        />
      </S.ScannerInputContainer>
      <S.ClearLogContainer>
        <input
          type="button"
          value="Clear"
          onClick={() => setTextFieldLog("")}
        />
      </S.ClearLogContainer>
      <S.ClearLogContainer>
        <input
          type="button"
          value="Clear"
          onClick={() => setScannerInputLog("")}
        />
      </S.ClearLogContainer>
      <S.TextFieldLogContainer>
        <TextField value={textFieldLog} multiline fullWidth />
      </S.TextFieldLogContainer>
      <S.ScannerInputLogContainer>
        <TextField value={scannerInputLog} multiline fullWidth />
      </S.ScannerInputLogContainer>
    </S.TestPage>
  );
};

export default TestPage;
